import { TypographyStyles } from '@/styles'
import { Text } from '@/ui'

/**
 * Definitions for the terms on the state breakdown, written for someone with no
 * tax background — the same plain-English framing the internal hand-check guide
 * uses, minus the jargon it has to define.
 */

export const StateStandardDeductionTooltip = ({
  filingState,
}: {
  filingState: string
}) => (
  <>
    <Text variant={TypographyStyles.H3}>
      Income {filingState} doesn&apos;t tax
    </Text>
    <br />
    <Text>
      Most states let you subtract a set amount from your income before working
      out the tax, so the first slice isn&apos;t taxed at all. The amount is set
      by {filingState} and depends on whether you file alone or with a spouse.
    </Text>
    <br />
    <Text>
      A few states don&apos;t offer this at all, in which case you&apos;ll see
      $0.00 here and all of the income is taxed.
    </Text>
  </>
)

export const StateTaxableIncomeTooltip = ({
  filingState,
}: {
  filingState: string
}) => (
  <>
    <Text variant={TypographyStyles.H3}>Income {filingState} taxes</Text>
    <br />
    <Text>
      What&apos;s left after subtracting the untaxed amount above. {filingState}{' '}
      applies its tax rates to this figure, not to your full income.
    </Text>
  </>
)

export const StateShareTooltip = ({ filingState }: { filingState: string }) => (
  <>
    <Text variant={TypographyStyles.H3}>
      Share of your income earned in {filingState}
    </Text>
    <br />
    <Text>
      You told us what percentage of your practice work happened in each state.{' '}
      {filingState} taxes only its percentage of your practice income — not your
      spouse&apos;s income, and not wages from a job.
    </Text>
    <br />
    <Text>
      The state you live in is the exception: it taxes everything your household
      earns.
    </Text>
  </>
)

export const StatePaymentsCreditedTooltip = ({
  filingState,
}: {
  filingState: string
}) => (
  <>
    <Text variant={TypographyStyles.H3}>
      {filingState} payments you&apos;ve already made
    </Text>
    <br />
    <Text>
      Quarterly payments you&apos;ve told us about for earlier quarters this
      year. They count toward what you owe {filingState}, so they lower this
      quarter&apos;s amount.
    </Text>
    <br />
    <Text>
      Payments to one state never lower another state&apos;s estimate, and we
      only count them once every earlier quarter has a payment recorded.
    </Text>
  </>
)

export const StateWithholdingTooltip = ({
  filingState,
}: {
  filingState: string
}) => (
  <>
    <Text variant={TypographyStyles.H3}>
      {filingState} tax from your paychecks
    </Text>
    <br />
    <Text>
      This is the state tax withheld from a W-2 job — the amount in box 17 of
      your W-2. If you have a job that takes tax out of your paycheck, that
      money already counts toward what you owe. We apply it to the state you
      live in, which is the state your employer withholds for.
    </Text>
  </>
)
